import { BigNum, PublicKey, UserStatsAccount } from '@drift-labs/sdk';
import { ACTION_HELPERS, NEW_ACCOUNT_BASE_RENT } from '@drift/common';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { format } from 'timeago.js';
import NumberDisplay from './NumberDisplay/NumberDisplay';

type AccountDisplayData = {
	authority: PublicKey;
	userId: number;
	accountName: string;
	acctSpotDeposits: BigNum;
	netUnsettled: BigNum;
	acctBorrows: BigNum;
	acctNetValue: BigNum;
	delegate?: PublicKey;
	health: number;
	pubKey: PublicKey;
	rent: {
		amount: BigNum;
		loaded: boolean;
	};
};

enum RECLAIMABLE_RENT_STATE {
	NO_EXCESS_RENT_TO_CLAIM,
	EXCESS_RENT_AVAILABLE_TO_CLAIM,
	EXCESS_RENT_AVAILABLE_AFTER_DELAY_PERIOD,
}

export function canClaimRent(
	userStatsAccount: UserStatsAccount | null | undefined,
	rentAmount: BigNum
): boolean {
	if (!userStatsAccount) return false;

	const accountIsOldEnoughToClaimRent =
		ACTION_HELPERS.ACCOUNT_DELETION_HELPERS.getStatsAccountIsPastDeletionCutoff(
			userStatsAccount
		);

	const excessRentInAccount = rentAmount.sub(NEW_ACCOUNT_BASE_RENT);
	const excessRentIsInAccount = excessRentInAccount.gtZero();

	const reclaimableRentState = excessRentIsInAccount
		? accountIsOldEnoughToClaimRent
			? RECLAIMABLE_RENT_STATE.EXCESS_RENT_AVAILABLE_TO_CLAIM
			: RECLAIMABLE_RENT_STATE.EXCESS_RENT_AVAILABLE_AFTER_DELAY_PERIOD
		: RECLAIMABLE_RENT_STATE.NO_EXCESS_RENT_TO_CLAIM;

	return (
		reclaimableRentState ===
		RECLAIMABLE_RENT_STATE.EXCESS_RENT_AVAILABLE_TO_CLAIM
	);
}

const ReclaimableRentContent = ({
	userStatsAccount,
	accountDisplayData,
	isMobile,
}: {
	userStatsAccount: UserStatsAccount;
	accountDisplayData: AccountDisplayData;
	isMobile?: boolean;
}) => {
	const estimatedClaimWaitTime = userStatsAccount
		? ACTION_HELPERS.ACCOUNT_DELETION_HELPERS.getStatsAccountDeletionWaitTime(
				userStatsAccount
			)
		: 0;

	const excessRentInAccount = accountDisplayData.rent.amount.sub(
		NEW_ACCOUNT_BASE_RENT
	);
	const excessRentIsInAccount = excessRentInAccount.gtZero();

	if (isMobile) {
		return <>{accountDisplayData.rent.amount.prettyPrint()}</>;
	}

	return (
		<Tooltip
			content={
				<>
					{excessRentIsInAccount && (
						<>
							<div className="flex items-center justify-between gap-x-sm">
								<span>Excess Rent:</span>
								<NumberDisplay
									value={excessRentInAccount}
									type="number"
									dimIfZero
									decimals={4}
									suffix="SOL"
								/>
							</div>
							<span>
								{`Claimable ${
									estimatedClaimWaitTime === 0
										? 'now'
										: format(
												new Date(Date.now() + estimatedClaimWaitTime * 1000)
											)
								}`}
							</span>
						</>
					)}

					<div className="flex items-center justify-between gap-x-sm">
						<span>Base Account Rent:</span>
						<span>
							<NumberDisplay
								value={NEW_ACCOUNT_BASE_RENT}
								type="number"
								dimIfZero
								decimals={4}
								suffix="SOL"
							/>
						</span>
					</div>
					<span>Requires account deletion</span>
				</>
			}
		>
			<NumberDisplay
				value={accountDisplayData.rent.amount}
				type="number"
				dimIfZero
				decimals={4}
				suffix="SOL"
			/>
		</Tooltip>
	);
};

export default ReclaimableRentContent;
